// Import validation utilities

// Email validation
export const isValidEmail = (email) => {
  if (!email || email.trim() === "") return true; // Empty is allowed
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Birthdate validation (accepts YYYY-MM-DD or DD.MM.YYYY format)
export const isValidBirthDate = (birthDate) => {
  if (!birthDate || birthDate.trim() === "") return true; // Empty is allowed

  const trimmed = birthDate.trim();

  // Check for YYYY-MM-DD format (ISO format)
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (isoRegex.test(trimmed)) {
    const date = new Date(trimmed);
    if (!(date instanceof Date) || isNaN(date)) return false;

    const [year, month, day] = trimmed.split('-').map(Number);
    return date.getFullYear() === year &&
           date.getMonth() + 1 === month &&
           date.getDate() === day;
  }

  // Check for DD.MM.YYYY format (German format)
  const germanRegex = /^\d{1,2}\.\d{1,2}\.\d{4}$/;
  if (germanRegex.test(trimmed)) {
    const [day, month, year] = trimmed.split('.').map(Number);

    // Basic range validation
    if (year < 1900 || year > 2100) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Create date and verify it's valid
    const date = new Date(year, month - 1, day);
    if (!(date instanceof Date) || isNaN(date)) return false;

    // Verify the date wasn't adjusted
    return date.getFullYear() === year &&
           date.getMonth() + 1 === month &&
           date.getDate() === day;
  }

  // Check for DD.MM.YY format (short year)
  const shortYearRegex = /^\d{1,2}\.\d{1,2}\.\d{2}$/;
  if (shortYearRegex.test(trimmed)) {
    const [day, month, shortYear] = trimmed.split('.').map(Number);

    // Assume 2000s for years 00-30, 1900s for 31-99
    const year = shortYear <= 30 ? 2000 + shortYear : 1900 + shortYear;

    // Basic range validation
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Create date and verify it's valid
    const date = new Date(year, month - 1, day);
    if (!(date instanceof Date) || isNaN(date)) return false;

    // Verify the date wasn't adjusted
    return date.getFullYear() === year &&
           date.getMonth() + 1 === month &&
           date.getDate() === day;
  }

  // Invalid format
  return false;
};

// Check if two students are duplicates (same name + birthdate)
export const isDuplicate = (student1, student2) => {
  const name1 = `${student1.firstName || ""} ${student1.lastName || ""}`.toLowerCase().trim();
  const name2 = `${student2.firstName || ""} ${student2.lastName || ""}`.toLowerCase().trim();
  const birth1 = (student1.birthDate || "").trim();
  const birth2 = (student2.birthDate || "").trim();

  // Must have same name
  if (name1 !== name2) return false;

  // If both have birthdate, they must match
  if (birth1 && birth2) {
    return birth1 === birth2;
  }

  // If name matches but no birthdate, consider it a potential duplicate
  return true;
};

// Validate a single student row
export const validateStudent = (student, rowIndex) => {
  const errors = [];
  const warnings = [];

  // Required fields
  if (!student.firstName || student.firstName.trim() === "") {
    errors.push(`Zeile ${rowIndex + 2}: Vorname fehlt`);
  }
  if (!student.lastName || student.lastName.trim() === "") {
    errors.push(`Zeile ${rowIndex + 2}: Nachname fehlt`);
  }

  // Email validation
  if (student.email && !isValidEmail(student.email)) {
    errors.push(`Zeile ${rowIndex + 2}: Ungültige E-Mail-Adresse: ${student.email}`);
  }

  // Birthdate validation
  if (student.birthDate && !isValidBirthDate(student.birthDate)) {
    warnings.push(`Zeile ${rowIndex + 2}: Ungültiges Geburtsdatum: ${student.birthDate}`);
  }

  // Adult-specific validation
  if (student.adult === true || student.adult === "Ja") {
    if (!student.skillLevel || student.skillLevel.trim() === "") {
      warnings.push(`Zeile ${rowIndex + 2}: Spielstärke fehlt für Erwachsenen`);
    }
  }

  // Children-specific validation
  if (student.adult === false || student.adult === "Nein") {
    if (!student.trainigGroup || student.trainigGroup.trim() === "") {
      warnings.push(`Zeile ${rowIndex + 2}: Trainingsgruppe fehlt für Kind`);
    }
  }

  return { errors, warnings };
};

// Validate entire import dataset
export const validateImport = (parsedStudents, existingStudents) => {
  const errors = [];
  const warnings = [];
  const duplicates = [];

  // Validate each student
  parsedStudents.forEach((student, index) => {
    const validation = validateStudent(student, index);
    errors.push(...validation.errors);
    warnings.push(...validation.warnings);
  });

  // Check for duplicates within CSV
  for (let i = 0; i < parsedStudents.length; i++) {
    for (let j = i + 1; j < parsedStudents.length; j++) {
      if (isDuplicate(parsedStudents[i], parsedStudents[j])) {
        duplicates.push({
          type: "within-csv",
          row1: i + 2,
          row2: j + 2,
          student: parsedStudents[i]
        });
      }
    }
  }

  // Check for duplicates against existing students
  parsedStudents.forEach((newStudent, index) => {
    existingStudents.forEach((existingStudent) => {
      if (isDuplicate(newStudent, existingStudent)) {
        duplicates.push({
          type: "existing",
          row: index + 2,
          student: newStudent,
          existingStudent
        });
      }
    });
  });

  return {
    errors,
    warnings,
    duplicates,
    totalRows: parsedStudents.length,
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0,
    hasDuplicates: duplicates.length > 0
  };
};

// Parse CSV row to student object
export const parseCSVRowToStudent = (row, headers) => {
  const student = {};

  headers.forEach((header, index) => {
    const value = row[index] || "";

    switch (header.toLowerCase().trim()) {
      case "vorname":
        student.firstName = value;
        break;
      case "nachname":
        student.lastName = value;
        break;
      case "geburtsdatum":
        student.birthDate = value;
        break;
      case "email":
        student.email = value;
        break;
      case "telefon":
        student.phone = value;
        break;
      case "adresse":
        student.adress = value;
        break;
      case "erwachsen":
        student.adult = value.toLowerCase() === "ja" || value === "true" || value === "1";
        break;
      case "mitglied":
        student.member = value.toLowerCase() === "ja" || value === "true" || value === "1";
        break;
      case "teamspieler":
        student.team = value.toLowerCase() === "ja" || value === "true" || value === "1";
        break;
      case "spielstärke":
        student.skillLevel = value;
        break;
      case "trainingsgruppe":
        student.trainigGroup = value;
        break;
      case "geschlecht":
        student.sex = value;
        break;
      case "häufigkeit":
        student.frequence = value;
        break;
      case "kommentar":
        student.comment = value;
        break;
      default:
        // Handle day columns (Montag, Dienstag, etc.)
        const days = ["montag", "dienstag", "mittwoch", "donnerstag", "freitag", "samstag"];
        const dayIndex = days.indexOf(header.toLowerCase().trim());
        if (dayIndex !== -1) {
          if (!student.availableTimes) student.availableTimes = [];
          // Parse hours from value (e.g., "14, 15, 16")
          if (value && value.trim()) {
            const hours = value.split(",").map(h => h.trim()).filter(h => h);
            const dayName = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][dayIndex];
            hours.forEach(hour => {
              student.availableTimes.push(`${dayName} ${hour}`);
            });
          }
        }
        break;
    }
  });

  return student;
};
